import { Skeleton } from "@volca/ui/components/ui/skeleton";
import { Stack } from "@volca/ui/components/ui/stack";
import { cn } from "@volca/ui/lib/utils";

/**
 * The side-by-side itself: today's rules beside the proposal.
 *
 * This is the shape the generic skeleton got most wrong. The pay comparison is
 * two spreadsheets, not a list, and thirty evenly spaced pills down the page
 * looked nothing like the thing arriving. It draws the split, both column
 * headers, the formula bar each side carries, and a cell grid.
 */
const HEADER = "shrink-0 border-b border-line bg-surface-sunken px-3 py-1.5";
const COLS = 6;
const ROWS = 22;

const Side = ({ title }: { title: string }) => (
  <Stack
    gap={0}
    align="stretch"
    direction="column"
    className="flex min-h-0 min-w-0 flex-col"
  >
    <p className={cn(HEADER, "text-xs font-medium tracking-widest uppercase")}>
      <span className="inline-block h-3 w-24 animate-pulse rounded-md bg-neutral" />
      <span className="sr-only">{title}</span>
    </p>
    {/* The formula bar, which is always there and always one line tall. */}
    <div className="shrink-0 border-b border-line bg-surface-sunken px-3 py-1.5">
      <Skeleton className="h-3 w-40" />
    </div>
    <div className="min-h-0 flex-1 overflow-hidden">
      {Array.from({ length: ROWS }, (_, row) => (
        <Stack
          gap={0}
          align="stretch"
          direction="row"
          key={row}
          className="flex border-b border-line"
        >
          {Array.from({ length: COLS }, (_, col) => (
            <div
              key={col}
              className={cn(
                "flex h-7 items-center border-r border-line px-2",
                // A row-number gutter, then even cells, the way the grid sits.
                { "w-10 shrink-0": col === 0, "min-w-0 flex-1": !(col === 0) },
              )}
            >
              <Skeleton
                className={cn("h-2.5", {
                  "w-4": col === 0,
                  "w-3/4": !(col === 0),
                })}
              />
            </div>
          ))}
        </Stack>
      ))}
    </div>
  </Stack>
);

export const ComparisonGridSkeleton = () => (
  <div
    role="status"
    aria-label="Loading the sheet…"
    className="grid min-h-0 flex-1 grid-cols-2 divide-x divide-line"
  >
    <Side title="Today's rules" />
    <Side title="Your proposal" />
  </div>
);
